import React, { Fragment, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Header from '../../../components/Header';
import Appfooter from '../../../components/Appfooter';
import Load from '../../../components/Load';
import { RELATORIO_AGENDAMENTOS, RELATORIO_AGENDAMENTOS_LOCAL, RELATORIO_AGENDAMENTOS_QUADRAS, QUADRAS_POR_LOCAL } from '../../../services/quadraAdminService';
import { QUADRA_POR_ID, STATUS_LABEL, formatCurrency, todayISO } from '../../../services/quadraService';

const AdminRelatorio: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const hoje = todayISO();
  const primeiroDiaMes = hoje.substring(0, 8) + '01';

  const [inicio, setInicio] = useState(primeiroDiaMes);
  const [fim, setFim] = useState(hoje);
  const [filtro, setFiltro] = useState({ inicio: primeiroDiaMes, fim: hoje });
  // Escopo do relatório: só esta quadra, todas as quadras do local, ou uma seleção de quadras.
  const [escopo, setEscopo] = useState<'quadra' | 'local' | 'selecao'>('quadra');
  // Quadras marcadas quando escopo = 'selecao'. Aplicadas apenas ao clicar em "Gerar".
  const [quadrasSelecionadas, setQuadrasSelecionadas] = useState<string[]>([]);
  const [quadrasAplicadas, setQuadrasAplicadas] = useState<string[]>([]);

  const { data: quadraData } = useQuery(QUADRA_POR_ID, { variables: { id } });
  const quadra = quadraData?.quadraPorId;
  const localId = quadra?.local?.id;

  // Lista de quadras do local para o seletor múltiplo.
  const { data: quadrasData } = useQuery(QUADRAS_POR_LOCAL, {
    variables: { localId },
    skip: !localId,
    fetchPolicy: 'cache-and-network',
  });
  const quadrasDoLocal: any[] = (quadrasData?.quadrasPorLocal ?? []).filter((q: any) => q.ativa);

  const {
    data: dataQuadra,
    loading: loadingQuadra,
    error: errorQuadra,
  } = useQuery(RELATORIO_AGENDAMENTOS, {
    variables: { quadraId: id, inicio: filtro.inicio, fim: filtro.fim },
    fetchPolicy: 'network-only',
    skip: escopo !== 'quadra',
  });

  const {
    data: dataLocal,
    loading: loadingLocal,
    error: errorLocal,
  } = useQuery(RELATORIO_AGENDAMENTOS_LOCAL, {
    variables: { localId, inicio: filtro.inicio, fim: filtro.fim },
    fetchPolicy: 'network-only',
    skip: escopo !== 'local' || !localId,
  });

  const {
    data: dataSelecao,
    loading: loadingSelecao,
    error: errorSelecao,
  } = useQuery(RELATORIO_AGENDAMENTOS_QUADRAS, {
    variables: { quadraIds: quadrasAplicadas, inicio: filtro.inicio, fim: filtro.fim },
    fetchPolicy: 'network-only',
    skip: escopo !== 'selecao' || quadrasAplicadas.length === 0,
  });

  const loading = escopo === 'quadra' ? loadingQuadra : escopo === 'local' ? loadingLocal : loadingSelecao;
  const error = escopo === 'quadra' ? errorQuadra : escopo === 'local' ? errorLocal : errorSelecao;
  const rel = escopo === 'quadra'
    ? dataQuadra?.relatorioAgendamentos
    : escopo === 'local'
    ? dataLocal?.relatorioAgendamentosLocal
    : dataSelecao?.relatorioAgendamentosQuadras;

  const toggleQuadra = (qid: string) => {
    setQuadrasSelecionadas(prev =>
      prev.includes(qid) ? prev.filter(x => x !== qid) : [...prev, qid]
    );
  };

  let porStatus: Record<string, number> = {};
  if (rel?.porStatus) {
    try {
      porStatus = typeof rel.porStatus === 'string' ? JSON.parse(rel.porStatus) : rel.porStatus;
    } catch {
      porStatus = {};
    }
  }

  const periodoLabel = `${filtro.inicio.split('-').reverse().join('/')} a ${filtro.fim.split('-').reverse().join('/')}`;

  // Rótulo do escopo para o subtítulo/impressão.
  const escopoLabel = escopo === 'quadra'
    ? ` / ${quadra?.nome ?? ''}`
    : escopo === 'local'
    ? ' / Todas as quadras'
    : (() => {
        const nomes = quadrasDoLocal
          .filter((q: any) => quadrasAplicadas.includes(q.id))
          .map((q: any) => q.nome);
        return nomes.length > 0 ? ` / ${nomes.join(', ')}` : ' / Quadras selecionadas';
      })();

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">

            <div className="d-flex align-items-center gap-2 mb-3 relatorio-no-print">
              <button className="btn btn-light btn-sm rounded-xl" onClick={() => navigate(-1)}>
                <i className="feather-arrow-left me-1"></i> Voltar
              </button>
              <div>
                <h2 className="fw-700 font-md text-grey-900 mb-0">Relatório de Agendamentos</h2>
                {quadra && (
                  <span className="font-xssss text-grey-500">
                    {quadra.local?.nome}{escopoLabel}
                  </span>
                )}
              </div>
            </div>

            {/* Cabeçalho visível apenas na impressão */}
            <div className="relatorio-print-header d-none">
              <h2>Relatório de Agendamentos</h2>
              <p>
                {quadra?.local?.nome}{escopoLabel}
                {' — '}Período: {periodoLabel}
              </p>
            </div>

            {/* Filtro de período + escopo */}
            <div className="card shadow-xss rounded-xxl border-0 p-4 mb-3 relatorio-no-print">
              <div className="d-flex flex-wrap align-items-end gap-3">
                <div>
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">De</label>
                  <input type="date" className="form-control rounded-xl font-xsss" value={inicio}
                    onChange={e => setInicio(e.target.value)} style={{ width: 160 }} />
                </div>
                <div>
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Até</label>
                  <input type="date" className="form-control rounded-xl font-xsss" value={fim}
                    onChange={e => setFim(e.target.value)} style={{ width: 160 }} />
                </div>
                <div>
                  <label className="font-xssss fw-600 text-grey-600 mb-1 d-block">Escopo</label>
                  <select
                    className="form-select rounded-xl font-xsss"
                    value={escopo}
                    onChange={e => setEscopo(e.target.value as 'quadra' | 'local' | 'selecao')}
                    style={{ width: 220 }}
                  >
                    <option value="quadra">Somente esta quadra</option>
                    <option value="local">Todas as quadras do local</option>
                    <option value="selecao">Selecionar quadras…</option>
                  </select>
                </div>
                <button
                  className="btn bg-primary-gradiant text-white rounded-xl font-xssss fw-600 px-4"
                  disabled={escopo === 'selecao' && quadrasSelecionadas.length === 0}
                  onClick={() => {
                    setFiltro({ inicio, fim });
                    if (escopo === 'selecao') setQuadrasAplicadas(quadrasSelecionadas);
                  }}
                >
                  <i className="feather-search me-1"></i> Gerar relatório
                </button>
                {rel && (
                  <button
                    className="btn btn-light rounded-xl font-xssss fw-600 px-4"
                    onClick={() => window.print()}
                  >
                    <i className="feather-printer me-1"></i> Imprimir
                  </button>
                )}
              </div>

              {/* Seleção múltipla de quadras */}
              {escopo === 'selecao' && (
                <div className="mt-3 pt-3 border-top">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <label className="font-xssss fw-600 text-grey-600 mb-0">Quadras incluídas no relatório</label>
                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-light btn-sm rounded-xl font-xssss"
                        onClick={() => setQuadrasSelecionadas(quadrasDoLocal.map((q: any) => q.id))}
                      >
                        Selecionar todas
                      </button>
                      <button
                        type="button"
                        className="btn btn-light btn-sm rounded-xl font-xssss"
                        onClick={() => setQuadrasSelecionadas([])}
                      >
                        Limpar
                      </button>
                    </div>
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    {quadrasDoLocal.length === 0 && (
                      <span className="font-xssss text-grey-500">Nenhuma quadra ativa neste local.</span>
                    )}
                    {quadrasDoLocal.map((q: any) => {
                      const marcada = quadrasSelecionadas.includes(q.id);
                      return (
                        <button
                          key={q.id}
                          type="button"
                          onClick={() => toggleQuadra(q.id)}
                          className={`btn rounded-xl font-xssss fw-600 px-3 py-1 ${marcada ? 'bg-primary-gradiant text-white' : 'btn-light'}`}
                        >
                          {marcada && <i className="feather-check me-1"></i>}
                          {q.nome}
                        </button>
                      );
                    })}
                  </div>
                  {quadrasSelecionadas.length === 0 && (
                    <p className="font-xssss text-grey-400 mb-0 mt-2">Selecione ao menos uma quadra e clique em "Gerar relatório".</p>
                  )}
                </div>
              )}
            </div>

            {loading && <Load />}
            {error && <div className="alert alert-danger font-xsss">{error.message}</div>}

            {rel && rel.total === 0 && (
              <div className="card shadow-xss w-100 p-5 text-center border-0 relatorio-print-area">
                <div className="relatorio-print-header d-none mb-3">
                  <h2>Relatório de Agendamentos</h2>
                  <p>
                    {quadra?.local?.nome}{escopoLabel}
                    {' — '}Período: {periodoLabel}
                  </p>
                </div>
                <span className="btn-round-xxl bg-greylight d-flex align-items-center justify-content-center mx-auto mb-3" style={{ width: 72, height: 72, borderRadius: '50%' }}>
                  <i className="feather-calendar text-grey-400" style={{ fontSize: 30 }}></i>
                </span>
                <h4 className="fw-700 font-xs text-grey-800 mb-2">Nenhum agendamento neste período</h4>
                <p className="text-grey-500 font-xssss mb-1">
                  Não encontramos registros para {escopo === 'quadra' ? 'esta quadra' : escopo === 'local' ? 'este local' : 'as quadras selecionadas'} entre <strong>{periodoLabel}</strong>.
                </p>
                <p className="text-grey-400 font-xssss mb-0">
                  Tente ampliar o período ou trocar o escopo do relatório.
                </p>
              </div>
            )}

            {rel && rel.total > 0 && (
              <div className="relatorio-print-area">
                {/* Cards de resumo */}
                <div className="row g-3 mb-3 align-items-stretch">
                  {[
                    { label: 'Total de agendamentos', value: String(rel.total), icon: 'feather-calendar', color: 'bg-primary-gradiant' },
                    { label: 'Total pago', value: formatCurrency(rel.totalPago), icon: 'feather-check-circle', color: 'bg-success' },
                    { label: 'Total pendente', value: formatCurrency(rel.totalPendente), icon: 'feather-clock', color: 'bg-warning' },
                    { label: 'Cancelados', value: String(rel.totalCancelados), icon: 'feather-x-circle', color: 'bg-danger' },
                  ].map((card, i) => (
                    <div key={i} className="col-lg-3 col-sm-6">
                      <div className="card shadow-xss rounded-xxl border-0 p-4 h-100">
                        <div className="d-flex align-items-center gap-3 h-100">
                          <span className={`btn-round-lg ${card.color} d-flex align-items-center justify-content-center flex-shrink-0`} style={{ minWidth: 48, minHeight: 48, borderRadius: '50%' }}>
                            <i className={`${card.icon} text-white font-sm`}></i>
                          </span>
                          <div className="d-flex flex-column justify-content-center">
                            <p className="font-xssss text-grey-500 mb-1">{card.label}</p>
                            <h4 className="fw-700 font-sm mb-0 text-grey-900">{card.value}</h4>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Por status */}
                {Object.keys(porStatus).length > 0 && (
                  <div className="card shadow-xss rounded-xxl border-0 p-4">
                    <h5 className="fw-700 font-xss mb-3">Por status</h5>
                    <div className="table-responsive">
                      <table className="table table-sm font-xsss">
                        <thead>
                          <tr className="text-grey-500"><th>Status</th><th>Quantidade</th></tr>
                        </thead>
                        <tbody>
                          {Object.entries(porStatus).map(([status, qtd]) => {
                            const st = STATUS_LABEL[status] ?? { label: status, color: 'secondary' };
                            return (
                              <tr key={status}>
                                <td><span className={`badge bg-${st.color} font-xssss rounded-xl`}>{st.label}</span></td>
                                <td className="fw-700">{qtd as number}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!loading && !rel && !error && (
              <div className="card shadow-xss w-100 p-5 text-center border-0">
                <i className="feather-bar-chart-2 font-xxl text-grey-300 d-block mb-2"></i>
                <p className="text-grey-500 font-xss mb-0">Selecione o período e clique em "Gerar relatório".</p>
              </div>
            )}

          </div>
        </div>
      </div>
      <Appfooter />
    </Fragment>
  );
};

export default AdminRelatorio;
